// Operation-plan block ontology (docs/19 §3.4 / §4). A plan's blocksJson is an
// array of BlockInstance ({ blockId, phase?, params, weight }); the field app
// advances block-by-block and phase-bearing blocks reuse the phase-tag pipeline
// (the backend reads `block.phase ?? block.params.phase`). SIM does not export a
// catalog, so the 14-block ST-2026 catalog is defined locally here.

import type { PlatoonPhase } from '../types/domain';

export type BlockCategory = 'movement' | 'hold' | 'action' | 'report';

export interface BlockDef {
  blockId: string;
  label: string;
  icon: string;
  category: BlockCategory;
  // If set, advancing this block tags the platoon's phase (via §3.4 pipeline).
  phase?: PlatoonPhase;
  hint: string;
}

export const BLOCK_CATALOG: BlockDef[] = [
  { blockId: 'SECURITY_POSTURE', label: 'Security Posture', icon: '🛡️', category: 'hold', phase: 'SECURITY', hint: 'Hold farmbase security before move-out.' },
  { blockId: 'MOVE_OUT', label: 'Move Out', icon: '🚶', category: 'movement', phase: 'MOVING_OUT', hint: 'Depart the start point / farmbase.' },
  { blockId: 'MOVE_TO_HIDEOUT', label: 'Move to Hideout', icon: '🧭', category: 'movement', hint: 'Route to the hideout / lay-up point.' },
  { blockId: 'OCCUPY_HIDEOUT', label: 'Occupy Hideout', icon: '⛺', category: 'hold', phase: 'HIDEOUT', hint: 'Establish and hold the hideout.' },
  { blockId: 'RECCE', label: 'Recce', icon: '🔭', category: 'action', phase: 'RECCE', hint: 'Reconnoitre the objective / route.' },
  { blockId: 'DRILLS', label: 'Rehearsal / Drills', icon: '🎯', category: 'action', phase: 'DRILLS', hint: 'Actions-on rehearsal before execution.' },
  { blockId: 'BRIEFING', label: 'Orders / Briefing', icon: '📋', category: 'action', phase: 'BRIEF', hint: 'Deliver orders / confirmatory brief.' },
  { blockId: 'MOVE_TO_TARGET', label: 'Move to Target', icon: '➡️', category: 'movement', hint: 'Final approach to the objective.' },
  { blockId: 'EXECUTE_TARGET', label: 'Execute on Target', icon: '💥', category: 'action', phase: 'EXECUTING', hint: 'Actions on the objective (raid/ambush/cache).' },
  { blockId: 'CONSOLIDATE', label: 'Consolidate', icon: '🧩', category: 'hold', hint: 'Reorg / consolidate on the objective.' },
  { blockId: 'FALLBACK', label: 'Fallback', icon: '↩️', category: 'movement', phase: 'FALLBACK', hint: 'Break contact / withdraw to a safe line.' },
  { blockId: 'MOVE_TO_FB', label: 'Move to Farmbase', icon: '🏠', category: 'movement', hint: 'Return route to the farmbase.' },
  { blockId: 'RECOVER', label: 'Recover', icon: '✅', category: 'hold', phase: 'RECOVERED', hint: 'Recovery complete — cycle ends.' },
  { blockId: 'REPORT_SITREP', label: 'SITREP', icon: '🗒️', category: 'report', hint: 'Send a situation report to HQ.' },
];

export const BLOCK_BY_ID: Record<string, BlockDef> = Object.fromEntries(
  BLOCK_CATALOG.map((b) => [b.blockId, b])
);

export interface PlanBlock {
  blockId: string;
  phase?: PlatoonPhase;
  params: Record<string, unknown>;
  weight: number;
}

export function blockDef(blockId: string): BlockDef | undefined {
  return BLOCK_BY_ID[blockId];
}

// A pragmatic default row for a newly-added block: phase auto-filled from the
// catalog, weight 1, empty note.
export function makeBlock(blockId: string): PlanBlock {
  const def = BLOCK_BY_ID[blockId];
  return {
    blockId,
    ...(def?.phase ? { phase: def.phase } : {}),
    params: {},
    weight: 1,
  };
}

// Parse a plan's blocksJson defensively into typed rows.
export function parsePlanBlocks(blocksJson: string | null | undefined): PlanBlock[] {
  if (!blocksJson) return [];
  try {
    const parsed = JSON.parse(blocksJson);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((b) => b && typeof b.blockId === 'string')
      .map((b) => ({
        blockId: b.blockId,
        phase: b.phase,
        params: b.params && typeof b.params === 'object' ? b.params : {},
        weight: typeof b.weight === 'number' && b.weight >= 1 ? b.weight : 1,
      }));
  } catch {
    return [];
  }
}
